import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  IconButton,
  Drawer as MuiDrawer,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import useStyles from "./styles";
import { useDispatch, useSelector } from "react-redux";
import { getApiData } from "../../utils/APIHelper";
import { Setting } from "../../utils/Setting";
import authActions from "../../redux/reducers/auth/actions";
import Cmodal from "../Cmodel";
import ConfirmModel from "../ConfirmModel";
import Images from "../../config/images";
import DrawerList from "./components/DrawerList";
import { Collapse, List, ListItem, ListItemText } from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  SettingsOutlined,
  Person,
} from "@mui/icons-material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { ReactComponent as LogoutActiveIcon } from "../../assets/svg/LogoutActiveIcon.svg";
import { ReactComponent as ProfileIcon } from "../../assets/svg/ProfileIcon.svg";
import { ReactComponent as ProfileActiveIcon } from "../../assets/svg/ProfileActiveIcon.svg";
import { useCRUDPermission } from "../hooks/usePermission";

const STAGING_OLD_URL =
  "https://reno-test-contractor.azurewebsites.net/dashboard";
const STAGING_NEW_URL = "https://contractor-portal-boq.azurewebsites.net/";

const getVersionInfo = () => {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  if (host === "localhost" || host === "127.0.0.1") {
    return { current: "new", oldUrl: STAGING_OLD_URL, newUrl: STAGING_NEW_URL };
  }
  if (host === "contractor-portal-boq.azurewebsites.net") {
    return { current: "new", oldUrl: STAGING_OLD_URL, newUrl: STAGING_NEW_URL };
  }
  if (host === "reno-test-contractor.azurewebsites.net") {
    return { current: "old", oldUrl: STAGING_OLD_URL, newUrl: STAGING_NEW_URL };
  }
  // Production is served from either renohome.io or renohome.ae, with the
  // "old" build on `contractor.*` and the "new" build on `contractor1.*`.
  // Keep the switch targets on whichever TLD the user is currently on so
  // switching versions doesn't cross domains (and lose the session).
  const prodMatch = host.match(/^(contractor1?)\.renohome\.(io|ae)$/);
  if (prodMatch) {
    const [, subdomain, tld] = prodMatch;
    return {
      current: subdomain === "contractor1" ? "new" : "old",
      oldUrl: `https://contractor.renohome.${tld}/`,
      newUrl: `https://contractor1.renohome.${tld}/`,
    };
  }
  return null;
};

const VersionToggle = () => {
  const info = getVersionInfo();
  if (!info) return null;
  const { current, oldUrl, newUrl } = info;

  const handleSwitch = (target) => {
    if (target === current) return;
    const url = target === "old" ? oldUrl : newUrl;
    window.location.href = url;
  };

  const baseBtn = {
    flex: 1,
    border: "none",
    background: "transparent",
    color: "#646F86",
    fontFamily: "Inter Tight",
    fontSize: "13px",
    fontWeight: 600,
    padding: "6px 0",
    cursor: "pointer",
    borderRadius: "6px",
    letterSpacing: 0,
    transition: "background 0.15s, color 0.15s",
  };
  const activeBtn = {
    ...baseBtn,
    background: "#4D6BF6",
    color: "#fff",
    cursor: "default",
  };

  return (
    <Box
      sx={{
        margin: "8px 18px 0",
        padding: "3px",
        display: "flex",
        background: "#F2F4F7",
        borderRadius: "8px",
      }}
    >
      <button
        type="button"
        onClick={() => handleSwitch("old")}
        style={current === "old" ? activeBtn : baseBtn}
      >
        Old
      </button>
      <button
        type="button"
        onClick={() => handleSwitch("new")}
        style={current === "new" ? activeBtn : baseBtn}
      >
        New
      </button>
    </Box>
  );
};

const DrawerComponent = () => {
  const currentUrl = window.location.href;
  const { pathname } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isActive = (path) => pathname === path;
  const { userData, accountTab } = useSelector((state) => state.auth);
  const { clearAllData, setAccountTab } = authActions;
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [mopen, setMopen] = useState("");
  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Check permission for account settings
  const { hasPermission: hasProfilePermission } = useCRUDPermission(
    "PROFILE",
    "READ",
  );

  const toggleProfileMenu = () => {
    setOpenProfileMenu(!openProfileMenu);
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  useEffect(() => {
    if (!currentUrl.includes("account-setting") && accountTab !== 0) {
      dispatch(setAccountTab(0));
    }
  }, [location]);

  // Add effect to manage body scroll when mobile drawer is open
  useEffect(() => {
    if (isMobile) {
      if (mobileDrawerOpen) {
        document.body.classList.add("drawer-open");
      } else {
        document.body.classList.remove("drawer-open");
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("drawer-open");
    };
  }, [mobileDrawerOpen, isMobile]);

  const handleClose = () => {
    setVisible(false);
  };

  const handleSignOut = () => setVisible(true);

  async function logout(type) {
    try {
      await getApiData(Setting.endpoints.logout, "POST");
      dispatch(clearAllData());
      handleClose();
      setTimeout(() => {
        type === "signup" ? navigate("/signup") : navigate("/login");
      }, 500);
    } catch (error) {
      console.log("🚀 logout ~ error:", error);
      // Force logout even if API call fails (e.g., network issues, server down)
      console.log("Forcing logout due to API failure");
      dispatch(clearAllData());
      handleClose();
      setTimeout(() => {
        type === "signup" ? navigate("/signup") : navigate("/login");
      }, 500);
    }
  }

  const DrawerContent = () => (
    <div
      className={classes.container}
      style={{
        display: "flex",
        flexDirection: "column",
        height: isMobile ? "100dvh" : "100vh",
        position: "relative",
        minHeight: isMobile ? "100dvh" : "100vh",
        overflow: "hidden",
      }}
    >
      <div
        className={classes.imgContainer}
        onClick={() => {
          if (userData?.contractor_data?.profile_completed === "pending") {
            return;
          } else {
            navigate("/dashboard");
            if (isMobile) {
              handleMobileDrawerClose();
            }
          }
        }}
      >
        <img alt="logo" src={Images.header_logo} className={classes.imgStyle} />
      </div>

      <VersionToggle />

      <div
        style={{
          padding: "10px 18px",
          color: "#C5CBD4",
          fontWeight: "500",
          fontSize: "14px",
          marginTop: "1vh",
        }}
      >
        MAIN
      </div>

      <div
        style={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
          msOverflowStyle: "none",
        }}
      >
        <DrawerList
          userData={userData}
          handleSignOut={handleSignOut}
          handleMobileDrawerClose={
            isMobile ? handleMobileDrawerClose : undefined
          }
          isMobile={isMobile}
        />
      </div>

      <div
        style={{
          flexShrink: 0,
          padding: "10px 10px",
          backgroundColor: "#fff",
          borderTop: "1px solid #f0f0f0",
        }}
      >
        {currentUrl?.includes("otp-verify") ? null : (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                gap: "50px",
              }}
              onClick={toggleProfileMenu}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {userData?.profile_url ? (
                  <img
                    alt="profile"
                    src={userData?.profile_url}
                    className={classes.logoStyle}
                  />
                ) : (
                  <Avatar style={{ color: "#FFF", cursor: "pointer" }} />
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#0D0D0E",
                    }}
                  >
                    {userData?.username}
                  </p>
                  <p
                    style={{
                      textTransform: "uppercase",
                      color: "#818898",
                      fontSize: "12px",
                    }}
                  >
                    {userData?.role}
                  </p>
                </div>
              </div>
              {openProfileMenu ? <ExpandLess /> : <ExpandMore />}
            </div>

            <Collapse in={openProfileMenu}>
              <List>
                {/* Account Settings - Only show if user has PROFILE READ
                    permission. Deliberately outside the !isMobile gate below:
                    the mobile drawer was left with Sign Out as its only entry,
                    so there was no way to reach account settings on a phone. */}
                {hasProfilePermission && (
                  <ListItem
                    button
                    onClick={() => {
                      navigate("/account-setting");
                      if (isMobile) {
                        handleMobileDrawerClose();
                      }
                    }}
                    style={{
                      color: isActive("/account-setting")
                        ? "#4D6BF6"
                        : "#949DAA",
                      fontWeight: "600",
                      fontSize: "14px",
                      padding: isMobile ? "10px 8px" : "1vh .4vw",
                    }}
                  >
                    <SettingsOutlined style={{ marginRight: 15 }} />
                    <ListItemText primary="Account Settings" />
                  </ListItem>
                )}

                {!isMobile && (
                  <>
                    {/* <ListItem
                      button
                      onClick={() => {
                        navigate("/contractor-profile");
                        if (isMobile) {
                          handleMobileDrawerClose();
                        }
                      }}
                      style={{
                        color: isActive("/contractor-profile")
                          ? "#4D6BF6"
                          : "#949DAA",
                        fontWeight: "600",
                        fontSize: "14px",
                        padding: "1vh .4vw",
                      }}
                    >
                      {isActive("/contractor-profile") ? (
                        <ProfileActiveIcon style={{ marginRight: 15 }} />
                      ) : (
                        <ProfileIcon style={{ marginRight: 15 }} />
                      )}
                      <ListItemText primary="Portfolio" />
                    </ListItem> */}

                    {userData?.role === "designer" ? (
                      <ListItem
                        button
                        onClick={() => {
                          navigate("/designer-documents");
                          if (isMobile) {
                            handleMobileDrawerClose();
                          }
                        }}
                        style={{
                          color: isActive("/designer-documents")
                            ? "#4D6BF6"
                            : "#949DAA",
                          fontWeight: "600",
                          fontSize: "14px",
                          padding: "1vh .4vw",
                        }}
                      >
                        <InsertDriveFileOutlinedIcon
                          style={{ marginRight: 15 }}
                        />
                        <ListItemText primary="Documents" />
                      </ListItem>
                    ) : userData?.role === "supplier" ? (
                      <ListItem
                        button
                        onClick={() => {
                          navigate("/supplier-documents");
                          if (isMobile) {
                            handleMobileDrawerClose();
                          }
                        }}
                        style={{
                          color: isActive("/supplier-documents")
                            ? "#4D6BF6"
                            : "#949DAA",
                          fontWeight: "600",
                          fontSize: "14px",
                          padding: "1vh .4vw",
                        }}
                      >
                        <InsertDriveFileOutlinedIcon
                          style={{ marginRight: 15 }}
                        />
                        <ListItemText primary="Documents" />
                      </ListItem>
                    ) : (
                      <></>
                    )}
                  </>
                )}

                <ListItem
                  button
                  onClick={handleSignOut}
                  style={{
                    color: "#F26B59",
                    fontWeight: "600",
                    fontSize: "14px",
                    padding: "1vh .5vw",
                  }}
                >
                  <LogoutActiveIcon style={{ marginRight: 18 }} />
                  <ListItemText primary="Sign Out" />
                </ListItem>
              </List>
            </Collapse>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {currentUrl?.includes("signup") ||
      currentUrl?.includes("login") ||
      currentUrl?.includes("otp-verify") ||
      currentUrl?.includes("create-profile") ||
      currentUrl?.includes("forgot-password") ||
      currentUrl?.includes("user-login") ||
      currentUrl?.includes("role") ||
      currentUrl?.includes("reset-password") ? null : (
        <>
          {isMobile ? (
            <>
              {/* Mobile Header with Hamburger */}
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1200,
                  backgroundColor: "#fff",
                  borderBottom: "1px solid #E5E5E5",
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: "60px",
                  boxSizing: "border-box",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <IconButton
                    onClick={handleMobileDrawerToggle}
                    sx={{
                      padding: "4px",
                      color: "#6B7280",
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                  <img
                    alt="logo"
                    src={Images.header_logo}
                    style={{ height: "24px", objectFit: "contain" }}
                  />
                </Box>
              </Box>

              {/* Mobile Drawer */}
              <MuiDrawer
                anchor="left"
                open={mobileDrawerOpen}
                onClose={handleMobileDrawerClose}
                PaperProps={{
                  sx: {
                    width: "100vw",
                    maxWidth: "320px",
                    backgroundColor: "#fff",
                    boxShadow: "2px 0 8px rgba(0, 0, 0, 0.15)",
                    height: "100vh",
                    height: "100dvh", // Use dynamic viewport height for mobile
                    display: "flex",
                    flexDirection: "column",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    zIndex: 1300,
                    touchAction: "pan-y",
                    WebkitOverflowScrolling: "touch",
                  },
                }}
                ModalProps={{
                  keepMounted: true,
                  disableScrollLock: false,
                  disableEscapeKeyDown: false,
                }}
                sx={{
                  "& .MuiDrawer-paper": {
                    boxSizing: "border-box",
                  },
                  "& .MuiBackdrop-root": {
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                  },
                }}
              >
                <DrawerContent />
              </MuiDrawer>
            </>
          ) : (
            <DrawerContent />
          )}
        </>
      )}

      <Cmodal
        visible={
          mopen === "currency" ? true : mopen === "language" ? true : false
        }
        handleClose={() => setMopen("")}
        currency={mopen}
      />
      <ConfirmModel
        visible={visible}
        handleClose={() => setVisible(false)}
        confirmation={() => logout("")}
        message="Are you sure you want to sign out?"
      />
    </div>
  );
};

export default DrawerComponent;
